// Catalog import: the importers assigned to an organization, what they have
// brought in, and the runs — the same tab on the admin's vendor page and in
// the vendor's own portal. The admin also assigns importers and keeps their
// settings; a vendor runs a sync and reads the statistics.
//
// What a run is: "Sync now" walks the whole source (new and changed
// products, and what is gone); "Check availability" only asks what is still
// in stock; "Sync selected" fetches the source ids typed in. The schedule
// does the first once a day and the second every few hours.
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
// the help center's article on the import (aldoo-com-api scripts/help-center/vendor/articles/catalog-import.html): its sections, by id
const HELP = { all: "vendor.import", offer: "vendor.import#offer", overview: "vendor.import#overview", sync: "vendor.import#sync", selected: "vendor.import#selected", schedule: "vendor.import#schedule", runs: "vendor.import#runs" }
import React, { useCallback, useEffect, useMemo, useState } from "react"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ArrowPathIcon, CheckCircleIcon, PlusCircleIcon, StopIcon, TrashIcon, SignalIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline"
import PubSub from "pubsub-js"
import useImportEvents from "./useImportEvents"
import {
  ListImporters, ListImportAssignments, AssignImporter, SaveImportAssignment, ScheduleImportAssignment, UnassignImporter, PingImport, SyncImport, ListImportRuns, GetImportRun, StopImportRun, ListSourceCategories, GetCategoryMap, SaveCategoryMap } from "../AdminShared/api"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Tabs from "aldoo-ra/Tabs"
import { ListCategories } from "../Storefront/api"
import { Section, Empty, Chip, Field, TextInput, NumberInput, Select, Toggle, Grid, dateTime, timeAgo, errorMessage } from "../AdminShared/ui"
import usePortalTexts from "./texts/usePortalTexts"
import { IMPORT } from "./texts/bundles"

const notify = (message, isError = false) => showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

const KIND = { discover: "Full sync", availability: "Availability check", items: "Selected products", rules: "Apply rules" }
const STATUS_TONE = { running: "bg-admin_info text-white", completed: "bg-admin_success text-white", failed: "bg-admin_error text-white", stopped: "bg-admin_neutral_3 text-white" }
const hours = (ms) => Math.round((ms || 0) / 3600000)

/** The counts of a run in one line: "3 new · 1 updated · 2 withdrawn" (t: the caller's usePortalTexts). */
const countsLine = (counts = {}, t = (s, ...args) => s.replace(/%s/g, () => args.shift())) => {
  const parts = [
    [counts.created, "%s new"], [counts.updated, "%s updated"], [counts.reclassified, "%s re-filed"], [counts.restored, "%s back in stock"], [counts.unavailable, "%s out of stock"], [counts.withdrawn, "%s withdrawn"], [counts.failed, "%s failed"], [counts.unchanged, "%s unchanged"],
  ].filter(([n]) => n)
  return parts.length ? parts.map(([n, label]) => t(label, n)).join(" · ") : t("%s read, nothing changed", counts.seen || 0)
}

// ---------------------------------------------------------------- settings (admin)

const idList = (value) => String(value || "").split(/[\s,;]+/).map((id) => id.trim()).filter(Boolean)

/**
 * The source's own categories, as a tree, with what the rules do to each:
 * Exclude takes a category and everything under it out of the import; Keep,
 * offered inside an excluded tree, lets a category stay as one of its own
 * under the root. A product left in none of them goes under "Other". The
 * two lists are the assignment's excludeCategories / keepCategories.
 */
function SourceCategories({ importer, assignment, onSaved, onApply, applying }) {
  const [list, setList] = useState(null)
  const [error, setError] = useState("")
  // where it all goes: the root category's path, as the setting names it
  const [ours, setOurs] = useState([])
  useEffect(() => {
    ListCategories("en").then((res) => setOurs(res?.list || [])).catch(() => setOurs([]))
  }, [])
  const rootLabel = useMemo(() => {
    const slug = String(assignment?.settings?.rootCategory || "").trim()
    if (!slug) return ""
    const byId = new Map(ours.map((c) => [c.categoryId, c]))
    const pathOf = (c, depth = 0) => {
      const parent = (c.parentCategoryIds || []).map((id) => byId.get(id)).find(Boolean)
      return parent && depth < 8 ? `${pathOf(parent, depth + 1)} › ${c.name}` : c.name
    }
    const node = byId.get(slug)
    return node ? pathOf(node) : slug
  }, [ours, assignment?.settings?.rootCategory])
  const defaults = useMemo(() => Object.fromEntries((importer?.settings || []).map((s) => [s.key, s.default])), [importer])
  const [excluded, setExcluded] = useState(() => new Set(idList(assignment?.settings?.excludeCategories ?? defaults.excludeCategories)))
  const [kept, setKept] = useState(() => new Set(idList(assignment?.settings?.keepCategories ?? defaults.keepCategories)))
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (!assignment?._id) return
    ListSourceCategories(assignment._id).then((res) => {
      const message = errorMessage(res)
      if (message) return setError(message)
      setList(res?.list || [])
    })
  }, [assignment?._id])

  const children = useMemo(() => {
    const map = new Map()
    for (const c of list || []) {
      const key = c.parentId && (list || []).some((p) => p.id === c.parentId) ? c.parentId : ""
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(c)
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name))
    return map
  }, [list])
  // a category with no product of theirs in it, nor under it, is left out of
  // the list (when the source says how many it holds)
  const counted = (list || []).some((c) => typeof c.products === "number")
  const total = useMemo(() => {
    const memo = new Map()
    const sum = (c) => {
      if (memo.has(c.id)) return memo.get(c.id)
      const own = typeof c.products === "number" ? c.products : 0
      const value = own + (children.get(c.id) || []).reduce((acc, child) => acc + sum(child), 0)
      memo.set(c.id, value)
      return value
    }
    return sum
  }, [children])

  const toggle = (set, setter, id) =>
    setter((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const save = async () => {
    setSaving(true)
    try {
      const res = await SaveImportAssignment(assignment._id, { settings: { excludeCategories: [...excluded].join(","), keepCategories: [...kept].join(",") } })
      const message = errorMessage(res)
      if (message) return notify(message, true)
      notify("Category rules saved — the next full sync applies them")
      onSaved?.(res.assignment)
    } finally {
      setSaving(false)
    }
  }

  // a row: its state follows its ancestors — inside an excluded tree it is
  // out unless kept, and a kept one opens its own tree again
  const rows = (parentId, depth, insideExcluded) =>
    (children.get(parentId) || []).flatMap((c) => {
      if (counted && total(c) === 0) return []
      const isExcluded = excluded.has(c.id)
      const isKept = insideExcluded && kept.has(c.id)
      const out = isExcluded || (insideExcluded && !isKept)
      return [
        <div key={c.id} className={`flex items-center gap-3 py-1.5 border-b border-admin_bordered/40 text-sm ${out ? "text-admin_text_secondary" : "text-admin_text"}`} style={{ paddingLeft: depth * 20 }} data-testid={`source-category-${c.id}`}>
          <span className={`flex-1 min-w-0 truncate ${out ? "line-through" : ""}`} title={`id ${c.id}`}>
            {c.name}
            {typeof c.products === "number" && <span className="ml-1.5 text-xs text-admin_text_secondary">{total(c)}</span>}
          </span>
          {insideExcluded && !isExcluded ? (
            <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
              <input type="checkbox" checked={isKept} onChange={() => toggle(kept, setKept, c.id)} className="accent-admin_primary" /> Keep
            </label>
          ) : (
            <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
              <input type="checkbox" checked={isExcluded} onChange={() => toggle(excluded, setExcluded, c.id)} className="accent-admin_primary" /> Exclude
            </label>
          )}
        </div>,
        ...rows(c.id, depth + 1, out),
      ]
    })

  return (
    <Section
      title="Source categories"
      subtitle={assignment?.categoryMapped ? "Filed through the category mapping below" : `Imported under: ${rootLabel || "a node named after the vendor"}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="admin_secondary" onClick={onApply} disabled={applying || !list} title="The products already here follow the saved rules: re-filed, the excluded categories removed" data-testid="source-categories-apply">{applying ? "Applying…" : "Apply rules"}</Button>
          <Button variant="admin_primary" onClick={save} disabled={saving || !list} data-testid="source-categories-save">{saving ? "Saving…" : "Save rules"}</Button>
        </div>
      }
    >
      <HelperTextLine>Exclude takes a category and everything under it out of the import. Keep, offered inside an excluded tree, lets one stay as a category of its own under the root. A product left in none of their categories goes under Other. Save rules keeps them for the next sync; Apply rules also re-files what is already here.</HelperTextLine>
      {error && <Empty>{error}</Empty>}
      {!list && !error && <Empty>Loading their categories…</Empty>}
      {list && !list.length && <Empty>The source lists no categories</Empty>}
      {list && list.length > 0 && <div className="max-h-[480px] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2">{rows("", 0, false)}</div>}
    </Section>
  )
}

const HelperTextLine = ({ children }) => <p className="m-0 text-xs text-admin_text_secondary">{children}</p>

/**
 * The store's categories, each by its path ("Books › Literature"), the
 * series terms left out: the options of a pick, and a slug's path.
 */
function useOurCategories() {
  const [categories, setCategories] = useState([])
  useEffect(() => {
    ListCategories("en").then((res) => setCategories(res?.list || [])).catch(() => setCategories([]))
  }, [])
  return useMemo(() => {
    const byId = new Map(categories.map((c) => [c.categoryId, c]))
    const pathOf = (c, depth = 0) => {
      const parent = (c.parentCategoryIds || []).map((id) => byId.get(id)).find(Boolean)
      return parent && depth < 8 ? `${pathOf(parent, depth + 1)} › ${c.name}` : c.name
    }
    const options = categories
      .filter((c) => c.kind !== "series")
      .map((c) => ({ value: c.categoryId, name: pathOf(c) }))
      .sort((a, b) => a.name.localeCompare(b.name))
    const nameOf = (slug) => (byId.has(slug) ? pathOf(byId.get(slug)) : slug)
    return { options, nameOf }
  }, [categories])
}

/**
 * A setting that names a category of ours (type "category"): a pick from the
 * store's category tree, each shown by its path ("Books › Literature"), the
 * series terms left out. The value is the category's slug.
 */
function CategorySetting({ value, onChange, testId, emptyLabel = "— under a node named after the vendor —" }) {
  const { options } = useOurCategories()
  return (
    <div data-testid={testId}>
      <DropDownSelect
        variant="admin"
        options={[{ value: "", name: emptyLabel }, ...options]}
        value={options.filter((o) => o.value === value)}
        onChange={(selection) => onChange(selection?.[0]?.value || "")}
        placeholder="Pick a category"
        searchEnabled
        allowEmptySelection={false}
        showCheckbox={false}
        showSelectionTick
      />
    </div>
  )
}

// ---------------------------------------------------------------- category mapping (admin)

// a path typed in: levels split by "›" (or ">"), each "bg | en"; "#slug" is a category of ours
const pathToText = (steps = []) =>
  steps.map((step) => (step.slug ? `#${step.slug}` : step.names?.en && step.names.en !== step.names.bg ? `${step.names.bg || step.names.en} | ${step.names.en}` : step.names?.bg || step.names?.en || "")).join(" › ")
const textToPath = (text) =>
  String(text || "")
    .split(/[›>]/)
    .map((level) => level.trim())
    .filter(Boolean)
    .map((level) => {
      if (/^#[\w-]+$/.test(level)) return { slug: level.slice(1) }
      const [bg, en] = level.split("|").map((part) => part.trim())
      return { names: en ? { bg, en } : { bg } }
    })

/** A row's target as the admin reads it: a pick by its path here, a level by its names. */
function TargetPath({ path, nameOf }) {
  if (!path?.length) return <span className="italic text-admin_text_secondary">nothing on its own</span>
  return (
    <span>
      {path.map((step, i) => (
        <span key={i}>
          {i > 0 && <span className="text-admin_text_secondary"> › </span>}
          {step.slug ? <span className="font-medium">{nameOf(step.slug)}</span> : <span>{step.names?.bg || step.names?.en}{step.names?.en && step.names?.bg && step.names.en !== step.names.bg && <span className="text-xs text-admin_text_secondary"> ({step.names.en})</span>}</span>}
        </span>
      ))}
    </span>
  )
}

/** A row's editor: a category of ours to start from (or the root), then the levels below it, typed. */
function TargetEditor({ row, onSave, onCancel, saving }) {
  const first = row.path?.[0]
  const [base, setBase] = useState(first?.slug || "")
  const [text, setText] = useState(pathToText(first?.slug ? row.path.slice(1) : row.path))
  const path = [...(base ? [{ slug: base }] : []), ...textToPath(text)]
  return (
    <div className="flex flex-col gap-2 p-3 my-1 border border-admin_bordered rounded-lg bg-admin_canvas" data-testid={`category-map-editor-${row.key}`}>
      <Grid cols={2}>
        <Field label="Under" hint="A category of ours, or the store's root">
          <CategorySetting value={base} onChange={setBase} emptyLabel="— the store's root —" testId="category-map-base" />
        </Field>
        <Field label="Then" hint="Levels below it, split by ›, each “bg | en” — matched by name, made when missing">
          <TextInput value={text} onChange={setText} placeholder="Тениски | T-shirts › Мъже | Men" data-testid="category-map-text" />
        </Field>
      </Grid>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!row.other && <Button variant="admin_secondary" onClick={() => onSave([])} disabled={saving} title="A grouping: its products are filed by their other categories, or under Other">File nothing by it</Button>}
        <Button variant="admin_secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="admin_primary" onClick={() => onSave(path)} disabled={saving || !path.length} data-testid="category-map-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  )
}

/**
 * Where the source's categories are filed here: each category of theirs, by
 * its path in their tree, to a chain of ours from the store's root — the
 * importer's default until an admin edits it (then the runs leave it alone),
 * back to the default by "Reset". The products follow at the next run;
 * "Apply rules" re-files the ones already here.
 */
function CategoryMapping({ assignment, onApply, applying }) {
  const [state, setState] = useState(null)
  const [error, setError] = useState("")
  const [editing, setEditing] = useState("")
  const [saving, setSaving] = useState("")
  const { nameOf } = useOurCategories()

  const load = useCallback(async () => {
    const res = await GetCategoryMap(assignment._id)
    const message = errorMessage(res)
    setError(message || "")
    if (!message) setState(res)
  }, [assignment._id])
  useEffect(() => {
    load()
  }, [load])

  const save = async (key, change) => {
    setSaving(key)
    try {
      const res = await SaveCategoryMap(assignment._id, change)
      const message = errorMessage(res)
      if (message) return notify(message, true)
      setState(res)
      setEditing("")
      notify("Category mapping saved — Apply rules re-files the products already here")
    } finally {
      setSaving("")
    }
  }
  const idOf = (row) => row.id ?? row.key

  if (state && !state.supported) return null
  return (
    <Section
      title="Category mapping"
      subtitle="Where each of their categories is filed here — shared with every vendor, matched by name"
      actions={<Button variant="admin_secondary" onClick={onApply} disabled={applying || !state} data-testid="category-map-apply">{applying ? "Applying…" : "Apply rules"}</Button>}
    >
      <HelperTextLine>A product is filed in the category its most specific category of theirs maps to. A row maps to a chain from the store's root; each level is a category of ours of that name under the one before, made when there is none. The importer keeps the rows nobody edited at its default; an edited row is yours until you reset it.</HelperTextLine>
      {error && <Empty>{error}</Empty>}
      {!state && !error && <Empty>Loading the mapping…</Empty>}
      {state?.rows?.length > 0 && (
        <div className="max-h-[560px] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2" data-testid="category-map">
          <div className="hidden md:flex gap-3 py-1.5 border-b border-admin_bordered text-xs font-medium text-admin_text_secondary">
            <span className="flex-1">Theirs</span>
            <span className="flex-1">Filed here under</span>
            <span className="w-[150px]" />
          </div>
          {state.rows.map((row) => (
            <div key={row.key} className="py-1.5 border-b border-admin_bordered/40 text-sm" data-testid={`category-map-row-${row.key}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
                <span className={`flex-1 min-w-0 truncate ${row.gone ? "line-through text-admin_text_secondary" : ""}`} title={`id ${idOf(row)}`}>
                  {row.other ? <span className="italic">No category of theirs</span> : row.source.join(" › ")}
                  {typeof row.products === "number" && <span className="ml-1.5 text-xs text-admin_text_secondary">{row.products}</span>}
                </span>
                <span className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
                  <TargetPath path={row.path} nameOf={nameOf} />
                  {row.edited && <Chip className="bg-admin_info text-white">edited</Chip>}
                  {!row.stored && <Chip className="bg-admin_neutral_3 text-white">default, not saved yet</Chip>}
                </span>
                {state.canEdit && (
                  <span className="w-[150px] flex justify-end gap-1.5">
                    {row.edited && row.default && <Button variant="admin_secondary" className="h-[28px] text-xs px-2" onClick={() => save(row.key, { reset: [idOf(row)] })} disabled={!!saving} title="Back to the importer's default" data-testid="category-map-reset">Reset</Button>}
                    <Button variant="admin_secondary" className="h-[28px] text-xs px-2" onClick={() => setEditing(editing === row.key ? "" : row.key)} disabled={!!saving} data-testid="category-map-edit">Edit</Button>
                  </span>
                )}
              </div>
              {editing === row.key && <TargetEditor row={row} saving={saving === row.key} onCancel={() => setEditing("")} onSave={(path) => save(row.key, { rows: { [idOf(row)]: path } })} />}
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

function SettingsForm({ importer, assignment, onSaved }) {
  const declared = importer?.settings || []
  const [values, setValues] = useState(() => ({ ...(assignment?.settings || {}) }))
  const [discoverEvery, setDiscoverEvery] = useState(hours(assignment?.discoverEvery) || 24)
  const [availabilityEvery, setAvailabilityEvery] = useState(hours(assignment?.availabilityEvery) || 4)
  const [initialStock, setInitialStock] = useState(assignment?.initialStock ?? 200)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      const res = await SaveImportAssignment(assignment._id, { settings: values, discoverEvery, availabilityEvery, initialStock })
      const error = errorMessage(res)
      if (error) return notify(error, true)
      notify("Settings saved")
      onSaved?.(res.assignment)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Grid cols={2}>
        {declared.map(({ key, label, description, secret, type, default: fallback }) => (
          <Field key={key} label={label} hint={description}>
            {type === "category" ? (
              <CategorySetting value={values[key] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [key]: v }))} testId={`import-setting-${key}`} />
            ) : (
              <TextInput type={secret ? "password" : "text"} value={values[key] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [key]: v }))} placeholder={secret ? "unchanged" : fallback || ""} data-testid={`import-setting-${key}`} />
            )}
          </Field>
        ))}
        <Field label="Full sync every (hours)" hint="How often the whole source is walked for new and changed products">
          <NumberInput value={discoverEvery} onChange={setDiscoverEvery} />
        </Field>
        <Field label="Availability check every (hours)" hint="How often what is still in stock is asked">
          <NumberInput value={availabilityEvery} onChange={setAvailabilityEvery} />
        </Field>
        <Field label="Initial stock" hint="What a new product starts with here; the vendor sets the real count after">
          <NumberInput value={initialStock} onChange={setInitialStock} />
        </Field>
      </Grid>
      <div className="flex justify-end">
        <Button variant="admin_primary" onClick={save} disabled={saving} data-testid="import-settings-save">{saving ? "Saving…" : "Save settings"}</Button>
      </div>
    </div>
  )
}

function AssignDialog({ orgId, importers, taken, onClose, onAssigned }) {
  const available = importers.filter((importer) => !taken.includes(importer.id))
  const [importerId, setImporterId] = useState(available[0]?.id || "")
  const importer = importers.find((i) => i.id === importerId)
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)

  const assign = async () => {
    if (!importerId) return
    setSaving(true)
    try {
      const settings = {}
      for (const { key, default: fallback } of importer?.settings || []) settings[key] = values[key] ?? fallback ?? ""
      const res = await AssignImporter(importerId, orgId, settings)
      const error = errorMessage(res)
      if (error) return notify(error, true)
      notify(`${importer?.name || importerId} assigned`)
      onAssigned?.(res.assignment)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4 border border-admin_bordered rounded-xl bg-admin_canvas" data-testid="assign-importer">
      <Typography variant="admin_h4" className="my-0">
        Assign an importer
      </Typography>
      {!available.length && <Empty>
        Every importer of this server is already assigned here
        </Empty>
      }
      {!!available.length && (
        <>
          <Field label="Importer">
            <Select value={importerId} onChange={setImporterId} options={available.map((i) => ({ value: i.id, label: i.name }))} />
          </Field>
          {importer?.description && <p className="text-sm text-admin_text_secondary my-0">
            {importer.description}
          </p>}
          <Grid cols={2}>
            {(importer?.settings || []).map(({ key, label, description, secret, default: fallback }) => (
              <Field key={key} label={label} hint={description}>
                <TextInput type={secret ? "password" : "text"} value={values[key] ?? fallback ?? ""} onChange={(v) => setValues((s) => ({ ...s, [key]: v }))} data-testid={`assign-setting-${key}`} />
              </Field>
            ))}
          </Grid>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button 
          variant="admin_secondary" 
          onClick={onClose}
        >
          Cancel
        </Button>

        {!!available.length && <Button 
          variant="admin_primary" 
          onClick={assign} 
          disabled={saving} 
          data-testid="assign-importer-confirm"
        >
          {saving ? "Assigning…" : "Assign"}
        </Button>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- runs

function RunRow({ run, onStop }) {
  const t = usePortalTexts(IMPORT)
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState(null)
  const toggle = async () => {
    setOpen((o) => !o)
    if (!details) {
      const res = await GetImportRun(run._id)
      if (!errorMessage(res)) setDetails(res.run)
    }
  }
  return (
    <div className="py-2" data-testid="import-run">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <button type="button" onClick={toggle} className="text-left font-medium hover:underline">{KIND[run.kind] ? t(KIND[run.kind]) : run.kind}</button>
        <Chip className={STATUS_TONE[run.status] || ""}>{t(run.status)}</Chip>
        <span className="text-admin_text_secondary">{countsLine(run.counts, t)}</span>
        <span className="flex-1" />
        <span className="text-xs text-admin_text_secondary" title={dateTime(run.startedAt)}>{timeAgo(run.startedAt, t)}{run.requestedBy ? "" : ` · ${t`scheduled`}`}</span>
        {run.status === "running" && (
          <Button variant="admin_secondary" className="h-[28px] text-xs px-2" onClick={() => onStop(run)} data-testid="import-run-stop"><StopIcon className="w-4 h-4" /> {t`Stop`}</Button>
        )}
      </div>
      {run.error && <div className="text-xs text-admin_error mt-1">{run.error}</div>}
      {open && (
        <div className="mt-2 ml-2 pl-3 border-l-2 border-admin_bordered text-xs" data-testid="import-run-entries">
          {!details && <Empty>{t`Loading…`}</Empty>}
          {details && !details.entries?.length && <Empty>{t`Nothing changed in this run`}</Empty>}
          {details?.entries?.map((entry, i) => (
            <div key={i} className="flex flex-wrap gap-x-2 py-0.5">
              <span className="font-mono">{entry.externalId}</span>
              <span className="font-medium">{entry.outcome}</span>
              {entry.pid && <span className="text-admin_text_secondary">{entry.pid}</span>}
              {entry.message && <span className="text-admin_text_secondary">— {entry.message}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- one assignment

function AssignmentCard({ assignment, importer, isAdmin, canSchedule = isAdmin, onChanged }) {
  const t = usePortalTexts(IMPORT)
  const [runs, setRuns] = useState([])
  const [ids, setIds] = useState("")
  const [busy, setBusy] = useState("")

  const loadRuns = useCallback(async () => {
    const res = await ListImportRuns(assignment._id, 1, 10)
    if (!errorMessage(res)) setRuns(res.list || [])
  }, [assignment._id])
  useEffect(() => {
    loadRuns()
  }, [loadRuns, assignment.lastRun, assignment.running])

  const act = async (label, fn, done) => {
    setBusy(label)
    try {
      const res = await fn()
      const error = errorMessage(res)
      if (error) return notify(error, true)
      done?.(res)
    } catch (e) {
      notify(e.message || t`Request failed`, true)
    } finally {
      setBusy("")
    }
  }
  const sync = (kind, list) => act(kind, () => SyncImport(assignment._id, { kind, ids: list }), () => { notify(t("%s started", t(KIND[list?.length ? "items" : kind]))); onChanged() })
  const ping = () => act("ping", () => PingImport(assignment._id), (res) => notify(res.ok ? (res.counts?.active ? t("The source answers: %s active products", res.counts.active) : t`The source answers`) : t("No answer: %s", res.message || res.error), !res.ok))
  const stop = (run) => act("stop", () => StopImportRun(run._id), () => { notify(t`Stopping…`); onChanged() })
  const toggle = (enabled) => act("enable", () => ScheduleImportAssignment(assignment._id, enabled), () => onChanged())
  const unassign = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Remove this importer?`,
      message: t("%s stops syncing %s. The %s imported products stay as they are.", importer?.name || assignment.importer, assignment.organization?.name || t`this organization`, assignment.products),
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: () => act("unassign", () => UnassignImporter(assignment._id), () => { notify(t`Importer removed`); onChanged() }),
    })

  const selected = ids.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
  const last = assignment.lastRun
  // what an admin sees, in three tabs: the sync and its runs, the settings, the categories
  const [view, setView] = useState("overview")
  const tabs = isAdmin
    ? [{ value: "overview", label: t`Overview` }, { value: "runs", label: t`Runs` }, { value: "settings", label: t`Settings` }, { value: "categories", label: t`Categories` }]
    : [{ value: "overview", label: t`Overview` }, { value: "runs", label: t`Runs` }]

  return (
    <Section
      title={importer?.name || assignment.importer}
      subtitle={importer?.description}
      actions={
        <div className="flex items-center gap-2">
          {assignment.running && 
            <Chip className="bg-admin_info text-white">
              {t`running`}
            </Chip>
          }
          {!assignment.enabled && 
            <Chip className="bg-admin_neutral_3 text-white">
              {t`paused`}
            </Chip>
          }
          {canSchedule && 
            <Toggle 
              checked={assignment.enabled} 
              onChange={toggle} 
              label={t`Scheduled`} 
              data-testid="import-enabled" 
            />
          }
          <HelpHint helpKey={HELP.schedule} />
          {isAdmin && 
            <Button 
              variant="admin_icon" 
              className="text-admin_text_secondary hover:text-admin_error" 
              onClick={unassign} 
              title={t`Remove this importer`} 
              aria-label="unassign-importer"
            >
              <TrashIcon className="w-5 h-5" />
            </Button>}
        </div>
      }
    >
      <Tabs tabs={tabs} value={view} onChange={setView} variant="admin" className="-mt-1" />

      {view === "settings" && <SettingsForm importer={importer} assignment={assignment} onSaved={() => onChanged()} />}
      {view === "categories" && <SourceCategories importer={importer} assignment={assignment} onSaved={() => onChanged()} onApply={() => sync("rules")} applying={busy === "rules" || assignment.running} />}
      {view === "categories" && <CategoryMapping assignment={assignment} onApply={() => sync("rules")} applying={busy === "rules" || assignment.running} />}

      {view === "overview" && (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm" data-testid="import-stats">
          <div>
            <Typography variant="admin_txt2_dim" className="flex items-center gap-1">
              {t`Products here`}
              <HelpHint helpKey={HELP.overview} />
            </Typography>
            <Typography 
              component="output" 
              variant="admin_h4" 
              className="block"
            >
              {assignment.products}
            </Typography>
          </div>
          <div>
            <Typography variant="admin_txt2_dim">
              {t`Withdrawn by the source`}
            </Typography>
            <Typography 
              component="output" 
              variant="admin_h4" 
              className="block"
            >
              {assignment.withdrawn}
            </Typography>
          </div>
          <div>
            <Typography variant="admin_txt2_dim">
              {t`Last full sync`}
            </Typography>
            <Typography variant="admin_body1_accent">
              {assignment.lastDiscoverAt 
                ? timeAgo(assignment.lastDiscoverAt, t) 
                : t`never`
              }
            </Typography>
            <Typography variant="admin_txt2">
              {t("next %s", assignment.next?.discover ? timeAgo(assignment.next.discover, t) : "-")}
            </Typography>
          </div>
          <div>
            <Typography variant="admin_txt2_dim">
              {t`Last availability check`}
            </Typography>
            <Typography variant="admin_body1_accent">
              {assignment.lastAvailabilityAt 
                ? timeAgo(assignment.lastAvailabilityAt, t) 
                : t`never`
              }
            </Typography>
            <Typography variant="admin_txt2">
              {t("next %s", assignment.next?.availability 
                ? timeAgo(assignment.next.availability, t) 
                : "-")
              }
            </Typography>
          </div>
        </div>

        {last && (
          <div className="text-sm mt-4">
            <span className="text-admin_text_secondary">
              {t`Last run:`}{" "}
            </span>
            {KIND[last.kind] ? t(KIND[last.kind]) : last.kind} · {t(last.status)} · {countsLine(last.counts, t)}
            
            {last.error && <span className="text-admin_error">
                            {" "}— {last.error}
                            </span>
            }
          </div>
        )}
        
        {assignment.lastError && !last?.error && 
          <Typography variant="admin_error">
            {assignment.lastError}
          </Typography>
        }

        <div className="flex flex-wrap items-center gap-2">
          <Button 
            variant="admin_primary" 
            onClick={() => sync("discover")} 
            disabled={!!busy || assignment.running} 
            data-testid="import-sync-now"
          >
            <ArrowPathIcon className="w-4 h-4" /> {t`Sync now`}
          </Button>
          <Button 
            variant="admin_secondary" 
            onClick={() => sync("availability")} 
            disabled={!!busy || assignment.running} 
            data-testid="import-check-availability"
          >
            <CheckCircleIcon className="w-4 h-4" /> 
            {t`Check availability`}
          </Button>
          <Button 
            variant="admin_secondary" 
            onClick={ping} 
            disabled={!!busy} 
            data-testid="import-ping"
          >
            <SignalIcon className="w-4 h-4" /> 
            {t`Test connection`}
          </Button>
          <HelpHint helpKey={HELP.sync} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[240px]" data-testid="import-ids">
            <Input 
              variant="admin" 
              value={ids} 
              onChange={(e) => setIds(e.target.value)} 
              placeholder={t`Source ids to sync, e.g. 2148, 2147`} 
              className="w-full" 
            />
          </div>
          <Button 
            variant="admin_secondary" 
            onClick={() => sync("items", selected)} 
            disabled={!selected.length || !!busy || assignment.running} 
            data-testid="import-sync-selected"
          >
            {t("Sync selected (%s)", selected.length)}
          </Button>
          <HelpHint helpKey={HELP.selected} />
        </div>
      </>)}

      {view === "runs" && (
        <div>
          <div className="flex justify-end"><HelpHint helpKey={HELP.runs} /></div>
          {!runs.length && <Empty>{t`No runs yet`}</Empty>}
          <div className="divide-y divide-admin_bordered/60">
            {runs.map((run) => <RunRow key={run._id} run={run} onStop={stop} />)}
          </div>
        </div>
      )}
    </Section>
  )
}

// ---------------------------------------------------------------- the tab

// canSchedule: the scheduled runs may be paused and resumed here (the admin, an admin of the organization)
/**
 * What a catalog import is, for a company that has none: its own shop keeps the
 * products, Aldoo follows it — and the way to ask for one, a chat with Aldoo
 * started with the request already written.
 */
function ImportOffer() {
  const t = usePortalTexts(IMPORT)
  const ask = () => PubSub.publish("show-chat", { draft: t`Hello, we would like to import our product catalog from our website. Our website is: ` })
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-admin_bordered bg-admin_neutral_1/40 p-4" data-testid="import-offer">
      <div className="text-base font-semibold inline-flex items-center gap-1.5">{t`Already selling on your own website?`}<HelpHint helpKey={HELP.offer} /></div>
      <p className="m-0 text-sm text-admin_text_secondary">
        {t`We can connect to your online shop and keep your products on Aldoo in sync with it: titles, descriptions, prices, images and stock are taken from your site and updated automatically, so you manage your products in one place only.`}
      </p>
      <ul className="m-0 pl-5 list-disc text-sm text-admin_text_secondary flex flex-col gap-0.5">
        <li>{t`For example: you run a WooCommerce or Shopify shop, have a product feed (XML or CSV), or a simple product listing page where we scan for availability — new products appear here, and a price you change there changes here too.`}</li>
        <li>{t`Aldoo sets up the import for you; you only tell us where your catalog is.`}</li>
      </ul>
      <div>
        <Button 
          variant="admin_primary" 
          onClick={ask} 
          data-testid="import-request-chat"
        >
          <span className="flex items-center gap-1.5">
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            {t`Request a catalog import`}
          </span>
        </Button>
      </div>
    </div>
  )
}

export default function ImportTab({ orgId, isAdmin = false, canSchedule = isAdmin }) {
  const t = usePortalTexts(IMPORT)
  const [assignments, setAssignments] = useState(null)
  const [importers, setImporters] = useState([])
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    const res = await ListImportAssignments(orgId)
    const err = errorMessage(res)
    setError(err || "")
    setAssignments(err ? [] : res.list || [])
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    if (!isAdmin) return
    ListImporters().then((res) => !errorMessage(res) && setImporters(res.list || []))
  }, [isAdmin])
  // a run moving or ending, an assignment changed: the cards follow
  useImportEvents({ orgId, onEvent: load })

  const importerOf = useMemo(() => Object.fromEntries(importers.map((i) => [i.id, i])), [importers])
  const taken = (assignments || []).map((a) => a.importer)

  return (
    <div className="flex flex-col gap-4" data-testid="import-tab">
      <div className="flex items-center justify-between">
        <div>
          <Typography component="h3" variant="admin_h3" className="flex items-center gap-1.5">
            {t`Catalog import`}
            <HelpHint helpKey={HELP.all} iconClassName="w-[18px] h-[18px]" />
          </Typography>
          <Typography variant="admin_txt1_dim">
            {t`Products brought in from the company's own catalog, kept in step with it.`}
          </Typography>
        </div>

        {isAdmin && !assigning && (
          <Button 
            variant="admin_primary" 
            onClick={() => setAssigning(true)} 
            data-testid="assign-importer-open"
          >
            <PlusCircleIcon className="w-5 h-5" /> {t`Assign importer`}
          </Button>
        )}
      </div>

      {isAdmin && assigning && <AssignDialog 
        orgId={orgId} 
        importers={importers} 
        taken={taken} 
        onClose={() => setAssigning(false)} 
        onAssigned={load} 
      />}

      {error && <div className="text-sm text-admin_error">{error}</div>}

      {assignments === null && <Empty>{t`Loading…`}</Empty>}
      {assignments?.length === 0 && !error && (isAdmin 
        ? <Empty>
            {t`No importer assigned to this organization yet`}
          </Empty> 
        : <ImportOffer />
      )}
      {assignments?.map((assignment) => (
        <AssignmentCard 
          key={assignment._id} 
          assignment={assignment} 
          importer={importerOf[assignment.importer] || { name: assignment.importerName }} 
          isAdmin={isAdmin} 
          canSchedule={canSchedule} 
          onChanged={load} 
        />
      ))}
    </div>
  )
}
